import AppLayout from '@/components/feature/AppLayout';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { formatINR, formatDateIST } from '@/utils/format';
import {
  fixedAssetsApi,
  FixedAsset,
  AssetCategory,
  AssetSummary,
  AssetDetailResponse,
  DepreciationPreviewResponse,
  DepreciationRunHistoryItem,
  VerificationSession,
  VerificationItem,
  ScheduleReportResponse,
} from '@/api/fixedAssetsApi';

type TabKey = 'register' | 'depreciation' | 'disposal' | 'verification' | 'reports';

export default function FixedAssetsPage() {
  const toast = useToast();

  // Navigation & Active Tab
  const [activeTab, setActiveTab] = useState<TabKey>('register');
  const [loading, setLoading] = useState(false);

  // Master Data
  const [summary, setSummary] = useState<AssetSummary | null>(null);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [selectedDept, setSelectedDept] = useState('');

  // Drawers & Modals
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetDetail, setAssetDetail] = useState<AssetDetailResponse | null>(null);
  const [detailTab, setDetailTab] = useState<'overview' | 'schedule' | 'timeline'>('overview');

  // Depreciation Tab State
  const [deprRunType, setDeprRunType] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');
  const [deprMonth, setDeprMonth] = useState<number>(new Date().getMonth() + 1);
  const [deprYear, setDeprYear] = useState<number>(new Date().getFullYear());
  const [deprPreview, setDeprPreview] = useState<DepreciationPreviewResponse | null>(null);
  const [deprRunsHistory, setDeprRunsHistory] = useState<DepreciationRunHistoryItem[]>([]);
  const [calculatingDepr, setCalculatingDepr] = useState(false);
  const [postingDepr, setPostingDepr] = useState(false);

  // Disposal Tab State
  const [disposalAssetId, setDisposalAssetId] = useState<string>('');
  const [disposalType, setDisposalType] = useState<'SALE' | 'SCRAP' | 'WRITE_OFF'>('SALE');
  const [disposalAmount, setDisposalAmount] = useState<number>(0);
  const [disposalBuyer, setDisposalBuyer] = useState('');
  const [disposalReason, setDisposalReason] = useState('');
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().slice(0, 10));
  const [disposing, setDisposing] = useState(false);

  // Verification Tab State
  const [activeSession, setActiveSession] = useState<VerificationSession | null>(null);
  const [verificationItems, setVerificationItems] = useState<VerificationItem[]>([]);
  const [startingVerif, setStartingVerif] = useState(false);
  const [completingVerif, setCompletingVerif] = useState(false);

  // Reports Tab State
  const [scheduleReport, setScheduleReport] = useState<ScheduleReportResponse | null>(null);

  // New Asset Form State
  const [newAsset, setNewAsset] = useState({
    assetName: '',
    assetCode: '',
    categoryId: '',
    description: '',
    subCategory: '',
    serialNumber: '',
    makeModel: '',
    purchaseDate: new Date().toISOString().slice(0, 10),
    purchaseCost: 0,
    additionalCost: 0,
    scrapValue: 0,
    department: 'Production',
    locationDetail: 'Main Shop Floor',
    custodianName: '',
    depreciationMethod: 'WDV' as 'WDV' | 'SLM',
    depreciationRate: 15,
    usefulLifeYears: 10,
    gstPaid: 0,
    insuranceCompany: '',
    insurancePolicyNo: '',
    insuranceExpiry: '',
    insuredValue: 0,
    postCapitalizationJv: true,
  });

  // ── Load Initial Master Data ──
  const fetchSummaryAndCategories = useCallback(async () => {
    try {
      const [sumData, catData] = await Promise.all([
        fixedAssetsApi.getSummary(),
        fixedAssetsApi.getCategories(),
      ]);
      setSummary(sumData);
      setCategories(catData);
      if (catData.length > 0 && !newAsset.categoryId) {
        setNewAsset((p) => ({
          ...p,
          categoryId: catData[0].id,
          depreciationRate: catData[0].it_depreciation_rate,
          depreciationMethod: catData[0].depreciation_method,
        }));
      }
    } catch (err: any) {
      console.error('Error loading fixed assets summary:', err);
    }
  }, [newAsset.categoryId]);

  const fetchAssetsList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fixedAssetsApi.getAssets({
        categoryId: selectedCategory,
        status: selectedStatus,
        department: selectedDept || undefined,
        search: search.trim() || undefined,
        limit: 100,
      });
      setAssets(res.assets);
      setTotalCount(res.total);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedStatus, selectedDept, search, toast]);

  useEffect(() => {
    fetchSummaryAndCategories();
  }, [fetchSummaryAndCategories]);

  useEffect(() => {
    fetchAssetsList();
  }, [fetchAssetsList]);

  // Load Asset Detail on Drawer Open
  const handleOpenDetail = async (assetId: string) => {
    try {
      setSelectedAssetId(assetId);
      setDetailDrawerOpen(true);
      setDetailTab('overview');
      const data = await fixedAssetsApi.getAssetById(assetId);
      setAssetDetail(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch asset details');
    }
  };

  // ── Fetch Tab-Specific Data on Tab Switch ──
  const handleTabSwitch = async (tab: TabKey) => {
    setActiveTab(tab);
    if (tab === 'depreciation') {
      try {
        const history = await fixedAssetsApi.getDepreciationRuns();
        setDeprRunsHistory(history);
      } catch (err) {}
    } else if (tab === 'reports') {
      try {
        const rep = await fixedAssetsApi.getScheduleReport();
        setScheduleReport(rep);
      } catch (err) {}
    }
  };

  // ── Register Asset Handler ──
  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAsset.assetName.trim()) {
      toast.error('Asset name is required');
      return;
    }
    if (!newAsset.categoryId) {
      toast.error('Asset category is required');
      return;
    }
    if (newAsset.purchaseCost <= 0) {
      toast.error('Please enter a valid purchase cost');
      return;
    }

    try {
      setLoading(true);
      const res = await fixedAssetsApi.createAsset({
        ...newAsset,
        purchaseCost: Number(newAsset.purchaseCost),
        additionalCost: Number(newAsset.additionalCost || 0),
        scrapValue: Number(newAsset.scrapValue || 0),
        depreciationRate: Number(newAsset.depreciationRate),
        usefulLifeYears: Number(newAsset.usefulLifeYears || 10),
        gstPaid: Number(newAsset.gstPaid || 0),
        insuredValue: Number(newAsset.insuredValue || 0),
      });

      toast.success(res.message || 'Asset registered successfully.');
      setRegisterModalOpen(false);
      await fetchSummaryAndCategories();
      await fetchAssetsList();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create asset');
    } finally {
      setLoading(false);
    }
  };

  // ── Depreciation Run Actions ──
  const handlePreviewDepreciation = async () => {
    if (deprRunType === 'MONTHLY' && (!deprMonth || isNaN(deprMonth) || deprMonth < 1 || deprMonth > 12)) {
      toast.error('Please select a valid month');
      return;
    }
    if (!deprYear || isNaN(deprYear) || deprYear < 1990) {
      toast.error('Please enter a valid year');
      return;
    }
    try {
      setCalculatingDepr(true);
      const prev = await fixedAssetsApi.previewDepreciation({
        runType: deprRunType,
        month: deprRunType === 'MONTHLY' ? deprMonth : undefined,
        year: deprYear,
      });
      setDeprPreview(prev);
      if (prev.alreadyRun) {
        toast.error(`Depreciation has already been executed for this period.`);
      } else if (prev.totalAssets === 0) {
        toast.info('No active assets require depreciation for this period.');
      } else {
        toast.success(`Calculated depreciation for ${prev.totalAssets} assets: ₹${prev.totalDepreciation.toLocaleString('en-IN')}`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Preview calculation failed');
    } finally {
      setCalculatingDepr(false);
    }
  };

  const handleExecuteDepreciation = async () => {
    if (deprRunType === 'MONTHLY' && (!deprMonth || isNaN(deprMonth) || deprMonth < 1 || deprMonth > 12)) {
      toast.error('Please select a valid month');
      return;
    }
    if (!deprYear || isNaN(deprYear) || deprYear < 1990) {
      toast.error('Please enter a valid year');
      return;
    }
    try {
      setPostingDepr(true);
      const res = await fixedAssetsApi.executeDepreciation({
        runType: deprRunType,
        month: deprRunType === 'MONTHLY' ? deprMonth : undefined,
        year: deprYear,
      });

      toast.success(res.message || 'Depreciation posted successfully.');
      setDeprPreview(null);
      const history = await fixedAssetsApi.getDepreciationRuns();
      setDeprRunsHistory(history);
      await fetchSummaryAndCategories();
      await fetchAssetsList();
    } catch (err: any) {
      toast.error(err.message || 'Depreciation posting failed');
    } finally {
      setPostingDepr(false);
    }
  };

  // ── Asset Disposal Actions ──
  const selectedDisposalAsset = useMemo(() => {
    return assets.find((a) => a.id === disposalAssetId) || null;
  }, [assets, disposalAssetId]);

  const disposalGainLoss = useMemo(() => {
    if (!selectedDisposalAsset) return 0;
    const saleVal = disposalType === 'SALE' ? Number(disposalAmount || 0) : 0;
    return Number((saleVal - selectedDisposalAsset.current_book_value).toFixed(2));
  }, [selectedDisposalAsset, disposalType, disposalAmount]);

  const handleConfirmDisposal = async () => {
    if (!disposalAssetId) {
      toast.error('Please select an asset to dispose');
      return;
    }
    if (disposalType === 'SALE' && disposalAmount <= 0) {
      toast.error('Please enter a valid sale proceeds amount');
      return;
    }

    try {
      setDisposing(true);
      const res = await fixedAssetsApi.disposeAsset(disposalAssetId, {
        disposalType,
        disposalDate,
        disposalAmount: Number(disposalAmount || 0),
        disposalBuyer: disposalBuyer.trim() || undefined,
        disposalReason: disposalReason.trim() || undefined,
      });

      toast.success(res.message || 'Asset disposed successfully.');
      setDisposalAssetId('');
      setDisposalAmount(0);
      setDisposalBuyer('');
      setDisposalReason('');
      await fetchSummaryAndCategories();
      await fetchAssetsList();
    } catch (err: any) {
      toast.error(err.message || 'Disposal failed');
    } finally {
      setDisposing(false);
    }
  };

  // ── Physical Verification Actions ──
  const handleStartVerification = async () => {
    try {
      setStartingVerif(true);
      const res = await fixedAssetsApi.startVerification();
      toast.success(res.message || 'Physical verification session started.');
      const sessionData = await fixedAssetsApi.getVerificationSession(res.data.id);
      setActiveSession(sessionData.session);
      setVerificationItems(sessionData.items);
    } catch (err: any) {
      toast.error(err.message || 'Failed to start verification');
    } finally {
      setStartingVerif(false);
    }
  };

  const handleToggleItemStatus = async (item: VerificationItem, condition: string) => {
    if (!activeSession) return;
    try {
      const updated = await fixedAssetsApi.updateVerificationItem(activeSession.id, item.id, {
        condition,
        status: condition === 'MISSING' ? 'MISSING' : 'VERIFIED',
      });
      setVerificationItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, condition: updated.condition, status: updated.status } : i))
      );
    } catch (err: any) {
      toast.error(err.message || 'Failed to update item');
    }
  };

  const handleCompleteVerification = async () => {
    if (!activeSession) return;
    try {
      setCompletingVerif(true);
      const res = await fixedAssetsApi.completeVerification(activeSession.id);
      toast.success(res.message || 'Verification audit completed and master records synchronized.');
      setActiveSession(null);
      setVerificationItems([]);
      await fetchSummaryAndCategories();
      await fetchAssetsList();
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete verification');
    } finally {
      setCompletingVerif(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (assets.length === 0) {
      toast.info('No assets to export');
      return;
    }
    const headers = 'Asset Code,Asset Name,Category,Cost,Acc Depreciation,Book Value,Rate,Method,Location,Custodian,Status\r\n';
    const rows = assets
      .map(
        (a) =>
          `"${a.asset_code}","${a.asset_name}","${a.category_name}",${a.total_capitalized_cost},${a.accumulated_depreciation},${a.current_book_value},"${a.depreciation_rate}%","${a.depreciation_method}","${a.location_detail || ''}","${a.custodian_name || ''}","${a.status}"`
      )
      .join('\r\n');
    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Fixed_Asset_Register_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Asset Register exported to CSV.');
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full space-y-5">
        {/* ── TOP COMMAND HEADER ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 shadow-sm">
                <i className="ri-building-2-line text-xl" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1e293b]">
                  Fixed Assets & Depreciation Command Center
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Track capital assets, calculate WDV/SLM depreciation, manage asset retirements & generate compliance schedules
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => setRegisterModalOpen(true)}
              style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
              className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer transition-all shadow-sm active:scale-95"
            >
              <i className="ri-add-line text-sm" />
              Register New Asset
            </button>
            <button
              onClick={() => handleTabSwitch('depreciation')}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-indigo-200 bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 text-sm font-medium cursor-pointer transition-colors"
            >
              <i className="ri-line-chart-line text-sm" />
              Run Depreciation
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium cursor-pointer transition-colors shadow-sm"
            >
              <i className="ri-download-2-line text-sm text-slate-500" />
              Export Register
            </button>
          </div>
        </div>

        {/* ── 6 SUMMARY KPI CARDS ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Assets</p>
            <p className="text-2xl font-bold text-[#1e293b] mt-1">
              {summary?.activeCount || assets.filter((a) => a.status === 'ACTIVE').length}
            </p>
            <p className="text-[11px] text-green-700 font-semibold mt-0.5 flex items-center gap-1">
              <i className="ri-checkbox-circle-fill text-xs" /> In Service
            </p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Gross Capitalized Cost</p>
            <p className="text-2xl font-bold text-[#1e293b] mt-1">
              {formatINR(summary?.grossBlock || 0)}
            </p>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Original Cost Basis</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider">Net Book Value (Today)</p>
            <p className="text-2xl font-bold text-indigo-700 mt-1 font-mono">
              {formatINR(summary?.netBlock || 0)}
            </p>
            <p className="text-[11px] text-indigo-900 font-medium mt-0.5">Balance Sheet Net Block</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Accumulated Depr.</p>
            <p className="text-2xl font-bold text-amber-700 mt-1 font-mono">
              {formatINR(summary?.totalDepreciation || 0)}
            </p>
            <p className="text-[11px] text-amber-900 font-medium mt-0.5">Total Written Off to P&L</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Physical Verification</p>
            <p className="text-2xl font-bold text-[#1e293b] mt-1">
              {summary?.dueForVerification || 0}
            </p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Due for Annual Audit</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Insurance Renewals</p>
            <p className="text-2xl font-bold text-purple-700 mt-1">
              {summary?.insuranceExpiringSoon || 0}
            </p>
            <p className="text-[11px] text-purple-900 font-medium mt-0.5">Expiring within 30 Days</p>
          </div>
        </div>

        {/* ── 5 TABS NAVIGATION BAR ── */}
        <div className="border-b border-[#e2e8f0] bg-white px-4 rounded-xl shadow-sm flex items-center justify-between">
          <div className="flex gap-2">
            {[
              { key: 'register', label: 'Asset Register (Master)', icon: 'ri-list-check-2' },
              { key: 'depreciation', label: 'Depreciation Hub & Runs', icon: 'ri-calculator-line' },
              { key: 'disposal', label: 'Asset Disposal & Retirements', icon: 'ri-exchange-dollar-line' },
              { key: 'verification', label: 'Physical Verification Audit', icon: 'ri-shield-check-line' },
              { key: 'reports', label: 'Tax & Balance Sheet Schedule', icon: 'ri-file-chart-line' },
            ].map((t) => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => handleTabSwitch(t.key as TabKey)}
                  className={`flex items-center gap-2 py-3.5 px-4 text-xs font-bold border-b-2 cursor-pointer transition-all ${
                    isActive
                      ? 'border-indigo-600 text-indigo-700 bg-indigo-50/40'
                      : 'border-transparent text-slate-600 hover:text-[#1e293b] hover:border-slate-300'
                  }`}
                >
                  <i className={`${t.icon} text-sm ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 1: ASSET REGISTER (MASTER LIST)                              */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'register' && (
          <div className="space-y-4">
            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3.5 rounded-xl border border-[#e2e8f0] shadow-sm">
              <div className="flex items-center gap-2.5 w-full sm:w-auto flex-1">
                <div className="relative w-full sm:w-80">
                  <i className="ri-search-line absolute left-3 top-2.5 text-xs text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by code, name, serial or model..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] font-medium"
                  />
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-semibold text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value="ALL">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.it_depreciation_rate}% WDV)
                    </option>
                  ))}
                </select>

                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-semibold text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="ACTIVE">Active (In Service)</option>
                  <option value="FULLY_DEPRECIATED">Fully Depreciated</option>
                  <option value="DISPOSED">Disposed / Sold</option>
                  <option value="SCRAPPED">Scrapped</option>
                </select>
              </div>

              <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                Showing {assets.length} of {totalCount} assets
              </span>
            </div>

            {/* Asset Table */}
            <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-4 py-3">Asset Code & Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3 text-right">Capitalized Cost</th>
                      <th className="px-4 py-3 text-right">Acc. Depreciation</th>
                      <th className="px-4 py-3 text-right">Net Book Value</th>
                      <th className="px-4 py-3">Location & Custodian</th>
                      <th className="px-4 py-3 text-center">Condition</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-slate-400">
                          <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                          Loading Fixed Asset Register...
                        </td>
                      </tr>
                    ) : assets.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-slate-400">
                          <i className="ri-inbox-line text-3xl mb-2 block text-slate-300" />
                          No fixed assets found matching your criteria.
                        </td>
                      </tr>
                    ) : (
                      assets.map((a) => {
                        const deprPercent = a.total_capitalized_cost > 0
                          ? Math.min(100, Math.round((a.accumulated_depreciation / a.total_capitalized_cost) * 100))
                          : 0;

                        return (
                          <tr key={a.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleOpenDetail(a.id)}
                                className="text-left group cursor-pointer"
                              >
                                <p className="font-mono font-bold text-indigo-600 group-hover:underline">
                                  {a.asset_code}
                                </p>
                                <p className="font-bold text-[#1e293b] line-clamp-1">{a.asset_name}</p>
                                {a.serial_number && (
                                  <p className="text-[10px] text-slate-400 font-mono">S/N: {a.serial_number}</p>
                                )}
                              </button>
                            </td>

                            <td className="px-4 py-3">
                              <span className="font-semibold text-slate-800">{a.category_name}</span>
                              <p className="text-[10px] text-slate-500">
                                {a.depreciation_method} @ {a.depreciation_rate}% p.a.
                              </p>
                            </td>

                            <td className="px-4 py-3 text-right font-mono font-semibold text-[#1e293b]">
                              {formatINR(a.total_capitalized_cost)}
                              <p className="text-[10px] text-slate-400">{formatDateIST(a.purchase_date)}</p>
                            </td>

                            <td className="px-4 py-3 text-right font-mono font-semibold text-amber-700">
                              {formatINR(a.accumulated_depreciation)}
                              <div className="w-16 h-1.5 bg-slate-200 rounded-full ml-auto mt-1 overflow-hidden">
                                <div
                                  className="h-full bg-amber-500 rounded-full"
                                  style={{ width: `${deprPercent}%` }}
                                />
                              </div>
                            </td>

                            <td className="px-4 py-3 text-right font-mono font-bold text-indigo-700">
                              {formatINR(a.current_book_value)}
                              <p className="text-[10px] text-slate-400">{100 - deprPercent}% remaining</p>
                            </td>

                            <td className="px-4 py-3">
                              <p className="font-medium text-[#1e293b] line-clamp-1">
                                {a.location_detail || a.department || '—'}
                              </p>
                              {a.custodian_name && (
                                <p className="text-[10px] text-slate-500">Resp: {a.custodian_name}</p>
                              )}
                            </td>

                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  a.condition === 'GOOD'
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : a.condition === 'FAIR'
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                    : a.condition === 'POOR'
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                                }`}
                              >
                                {a.condition}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide ${
                                  a.status === 'ACTIVE'
                                    ? 'bg-green-100 text-green-800'
                                    : a.status === 'FULLY_DEPRECIATED'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-slate-100 text-slate-500 line-through'
                                }`}
                              >
                                {a.status}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => handleOpenDetail(a.id)}
                                  className="h-7 px-2.5 rounded-lg border border-[#e2e8f0] hover:bg-slate-100 text-slate-700 text-xs font-bold cursor-pointer"
                                  title="View Details & Schedule"
                                >
                                  Details
                                </button>
                                {a.status === 'ACTIVE' && (
                                  <button
                                    onClick={() => {
                                      setDisposalAssetId(a.id);
                                      handleTabSwitch('disposal');
                                    }}
                                    className="h-7 px-2.5 rounded-lg border border-red-200 bg-red-50/50 hover:bg-red-100 text-red-700 text-xs font-bold cursor-pointer"
                                    title="Dispose or Scrap Asset"
                                  >
                                    Dispose
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 2: DEPRECIATION HUB & RUNS                                    */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'depreciation' && (
          <div className="space-y-6">
            {/* Run Depreciation Wizard Card */}
            <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] space-y-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                    <i className="ri-calculator-line text-indigo-600 text-lg" />
                    Automated Depreciation Engine (Companies Act & IT Act)
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Select a period to calculate monthly or annual WDV/SLM depreciation with pro-rata rules and atomic General Ledger posting.
                  </p>
                </div>
              </div>

              {/* Form Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-[#e2e8f0]">
                <div>
                  <label className="text-[11px] font-bold text-slate-600 block mb-1">Execution Frequency</label>
                  <select
                    value={deprRunType}
                    onChange={(e) => setDeprRunType(e.target.value as any)}
                    className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none"
                  >
                    <option value="MONTHLY">Monthly Run (Regular P&L)</option>
                    <option value="ANNUAL">Annual Run (ITR / Year End)</option>
                  </select>
                </div>

                {deprRunType === 'MONTHLY' && (
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">Period Month</label>
                    <select
                      value={deprMonth}
                      onChange={(e) => setDeprMonth(Number(e.target.value))}
                      className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none"
                    >
                      {[
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ].map((m, idx) => (
                        <option key={idx + 1} value={idx + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-[11px] font-bold text-slate-600 block mb-1">Calendar Year</label>
                  <input
                    type="number"
                    value={deprYear}
                    onChange={(e) => setDeprYear(Number(e.target.value))}
                    className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <button
                    onClick={handlePreviewDepreciation}
                    disabled={calculatingDepr}
                    style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                    className="flex-1 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer transition-colors shadow-sm disabled:opacity-50"
                  >
                    {calculatingDepr ? 'Calculating...' : 'Preview Calculation'}
                  </button>
                </div>
              </div>

              {/* Pre-Run Calculation Preview Table */}
              {deprPreview && (
                <div className="space-y-4 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-[#1e293b]">
                        Pre-Run Calculation Preview ({deprPreview.totalAssets} Active Assets)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Review numbers before committing. Total Depreciation to post: <span className="font-bold text-indigo-700">{formatINR(deprPreview.totalDepreciation)}</span>
                      </p>
                    </div>

                    <button
                      onClick={handleExecuteDepreciation}
                      disabled={postingDepr || deprPreview.alreadyRun || deprPreview.totalAssets === 0}
                      className="h-9 px-5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium cursor-pointer transition-all shadow-sm active:scale-95 disabled:opacity-50"
                    >
                      {postingDepr ? 'Posting to GL...' : 'Confirm & Post Depreciation JV'}
                    </button>
                  </div>

                  <div className="border border-[#e2e8f0] rounded-xl overflow-hidden max-h-72 overflow-y-auto text-xs">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold sticky top-0">
                        <tr>
                          <th className="px-4 py-2.5">Asset Code & Name</th>
                          <th className="px-4 py-2.5">Category</th>
                          <th className="px-4 py-2.5">Method & Rate</th>
                          <th className="px-4 py-2.5 text-right">Opening Book Value</th>
                          <th className="px-4 py-2.5 text-right">Period Depreciation</th>
                          <th className="px-4 py-2.5 text-right">Closing Book Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {deprPreview.lines.map((l) => (
                          <tr key={l.assetId} className="hover:bg-slate-50">
                            <td className="px-4 py-2 font-semibold text-[#1e293b]">
                              <span className="font-mono text-indigo-600 font-bold mr-1.5">{l.assetCode}</span>
                              {l.assetName}
                            </td>
                            <td className="px-4 py-2">{l.categoryName}</td>
                            <td className="px-4 py-2 font-mono">{l.method} @ {l.depreciationRate}%</td>
                            <td className="px-4 py-2 text-right font-mono">{formatINR(l.openingBookValue)}</td>
                            <td className="px-4 py-2 text-right font-mono font-bold text-amber-700">-{formatINR(l.depreciationAmount)}</td>
                            <td className="px-4 py-2 text-right font-mono font-bold text-indigo-700">{formatINR(l.closingBookValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* General Ledger Double-Entry Preview */}
                  <div className="bg-indigo-50/50 border border-indigo-200/70 rounded-xl p-4 text-xs space-y-2">
                    <p className="font-bold text-indigo-950 flex items-center gap-1.5">
                      <i className="ri-book-read-line" />
                      Automatic General Ledger Double-Entry Posting Preview:
                    </p>
                    <div className="divide-y divide-indigo-100 text-[11px]">
                      {deprPreview.categorySummaries.map((cat, i) => (
                        <div key={i} className="py-1.5 flex justify-between items-center font-mono">
                          <span className="text-red-700">Dr Depreciation Expense ({cat.categoryName}): {formatINR(cat.totalAmount)}</span>
                          <span className="text-green-700">Cr Accumulated Depreciation ({cat.categoryName}): {formatINR(cat.totalAmount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Past Depreciation Runs History */}
            <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] space-y-4">
              <h3 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                <i className="ri-history-line text-slate-500" />
                Past Depreciation Runs Archive
              </h3>

              <div className="border border-[#e2e8f0] rounded-xl overflow-hidden text-xs">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-4 py-3">Run Date</th>
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 text-right">Assets Processed</th>
                      <th className="px-4 py-3 text-right">Total Depreciation Amount</th>
                      <th className="px-4 py-3">Journal Voucher #</th>
                      <th className="px-4 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {deprRunsHistory.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-400">
                          No past depreciation runs found.
                        </td>
                      </tr>
                    ) : (
                      deprRunsHistory.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-[#1e293b]">{formatDateIST(r.run_date)}</td>
                          <td className="px-4 py-3 font-mono">
                            {r.period_month ? `Month ${r.period_month}/${r.period_year}` : r.financial_year}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700">{r.run_type}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total_assets_processed}</td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-amber-700">
                            {formatINR(r.total_depreciation_amount)}
                          </td>
                          <td className="px-4 py-3 font-mono font-bold text-indigo-600">{r.jv_number || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
                              {r.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 3: ASSET DISPOSAL & RETIREMENTS                              */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'disposal' && (
          <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] max-w-4xl mx-auto space-y-6">
            <div>
              <h2 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                <i className="ri-exchange-dollar-line text-indigo-600 text-xl" />
                Asset Disposal & Capital Gain/Loss Settlement
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Record asset sale, scrap, or write-off. System auto-calculates profit/loss and posts the full balanced disposal Journal Voucher.
              </p>
            </div>

            <div className="space-y-4">
              {/* Asset Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Select Asset to Dispose</label>
                <select
                  value={disposalAssetId}
                  onChange={(e) => setDisposalAssetId(e.target.value)}
                  className="w-full h-10 px-3 bg-white border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Choose an active fixed asset...</option>
                  {assets
                    .filter((a) => a.status === 'ACTIVE' || a.status === 'FULLY_DEPRECIATED')
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.asset_code} — {a.asset_name} (Book Value: {formatINR(a.current_book_value)})
                      </option>
                    ))}
                </select>
              </div>

              {selectedDisposalAsset && (
                <>
                  {/* Current Asset Status Info */}
                  <div className="grid grid-cols-3 gap-3 bg-slate-50 p-4 rounded-xl border border-[#e2e8f0] text-xs">
                    <div>
                      <p className="text-slate-500">Gross Capitalized Cost:</p>
                      <p className="font-mono font-bold text-[#1e293b] text-sm">
                        {formatINR(selectedDisposalAsset.total_capitalized_cost)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Accumulated Depreciation:</p>
                      <p className="font-mono font-bold text-amber-700 text-sm">
                        {formatINR(selectedDisposalAsset.accumulated_depreciation)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Current Net Book Value:</p>
                      <p className="font-mono font-bold text-indigo-700 text-base">
                        {formatINR(selectedDisposalAsset.current_book_value)}
                      </p>
                    </div>
                  </div>

                  {/* Disposal Inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Disposal Method</label>
                      <select
                        value={disposalType}
                        onChange={(e) => setDisposalType(e.target.value as any)}
                        className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      >
                        <option value="SALE">Sale to Third Party</option>
                        <option value="SCRAP">Scrap / Zero Value Dump</option>
                        <option value="WRITE_OFF">Write-Off (Theft / Total Loss)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Disposal Date</label>
                      <input
                        type="date"
                        value={disposalDate}
                        onChange={(e) => setDisposalDate(e.target.value)}
                        className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        {disposalType === 'SALE' ? 'Sale Proceeds (₹)' : 'Scrap Value Received (₹)'}
                      </label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={disposalAmount || ''}
                        onChange={(e) => setDisposalAmount(Number(e.target.value))}
                        className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Buyer / Purchaser Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Scrap Traders Ltd"
                        value={disposalBuyer}
                        onChange={(e) => setDisposalBuyer(e.target.value)}
                        className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Reason for Retirement</label>
                      <input
                        type="text"
                        placeholder="e.g. Upgraded to newer machine model"
                        value={disposalReason}
                        onChange={(e) => setDisposalReason(e.target.value)}
                        className="w-full h-9 px-3 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  {/* Profit / Loss Financial Impact Card */}
                  <div
                    className={`p-4 rounded-xl border flex items-center justify-between ${
                      disposalGainLoss >= 0
                        ? 'bg-green-50/80 border-green-200 text-green-900'
                        : 'bg-red-50/80 border-red-200 text-red-900'
                    }`}
                  >
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider">
                        {disposalGainLoss >= 0 ? '✓ Capital Gain / Profit on Sale' : '⚠ Loss on Disposal of Asset'}
                      </p>
                      <p className="text-[11px] opacity-80 mt-0.5">
                        Sale Amount ({formatINR(disposalAmount)}) - Net Book Value ({formatINR(selectedDisposalAsset.current_book_value)})
                      </p>
                    </div>
                    <span className="text-xl font-bold font-mono">
                      {formatINR(Math.abs(disposalGainLoss))}
                    </span>
                  </div>

                  {/* Disposal Button */}
                  <div className="pt-2 flex justify-end">
                    <button
                      onClick={handleConfirmDisposal}
                      disabled={disposing}
                      className="h-10 px-6 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold cursor-pointer transition-all shadow-sm active:scale-95 disabled:opacity-50"
                    >
                      {disposing ? 'Processing Disposal...' : 'Confirm Disposal & Post Journal Voucher'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 4: PHYSICAL VERIFICATION AUDIT                                */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'verification' && (
          <div className="space-y-6">
            {!activeSession ? (
              <div className="bg-white p-8 rounded-xl border border-[#e2e8f0] text-center max-w-xl mx-auto space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 mx-auto text-2xl">
                  <i className="ri-shield-check-line" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1e293b]">Annual Physical Asset Verification Audit</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Conduct annual statutory audit of all physical plant, machines, and IT hardware. Mark condition (Good, Fair, Damaged, Missing) and detect location movements.
                  </p>
                </div>
                <button
                  onClick={handleStartVerification}
                  disabled={startingVerif}
                  style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                  className="h-10 px-6 rounded-xl bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 cursor-pointer transition-all shadow-sm"
                >
                  {startingVerif ? 'Starting Audit...' : 'Start New Physical Verification Session'}
                </button>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                      <span className="font-mono text-indigo-600">Audit Session: {activeSession.verification_code}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                        {activeSession.status}
                      </span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Verify physical existence and operational condition for {verificationItems.length} assets.
                    </p>
                  </div>

                  <button
                    onClick={handleCompleteVerification}
                    disabled={completingVerif}
                    className="h-9 px-5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium cursor-pointer transition-all shadow-sm"
                  >
                    {completingVerif ? 'Synchronizing...' : 'Complete & Synchronize Master Records'}
                  </button>
                </div>

                {/* Audit Checklist Table */}
                <div className="border border-[#e2e8f0] rounded-xl overflow-hidden text-xs">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-[#e2e8f0]">
                      <tr>
                        <th className="px-4 py-3">Asset Code & Name</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3">Expected Location</th>
                        <th className="px-4 py-3 text-center">Condition Status</th>
                        <th className="px-4 py-3 text-center">Audit Verification</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {verificationItems.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-semibold text-[#1e293b]">
                            <span className="font-mono text-indigo-600 font-bold mr-1.5">{item.asset_code}</span>
                            {item.asset_name}
                          </td>
                          <td className="px-4 py-2.5">{item.category_name}</td>
                          <td className="px-4 py-2.5 text-slate-600">{item.expected_location || '—'}</td>
                          <td className="px-4 py-2.5 text-center">
                            <div className="inline-flex rounded-lg border border-[#e2e8f0] p-0.5 bg-slate-50">
                              {(['GOOD', 'FAIR', 'POOR', 'DAMAGED', 'MISSING'] as const).map((cond) => (
                                <button
                                  key={cond}
                                  onClick={() => handleToggleItemStatus(item, cond)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold transition-colors cursor-pointer ${
                                    item.condition === cond
                                      ? cond === 'MISSING' || cond === 'DAMAGED'
                                        ? 'bg-red-600 text-white'
                                        : 'bg-[#4f46e5] text-white'
                                      : 'text-slate-600 hover:text-[#1e293b]'
                                  }`}
                                >
                                  {cond}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                item.status === 'VERIFIED'
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : item.status === 'MISSING'
                                  ? 'bg-red-50 text-red-700 border border-red-200'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB 5: REPORTS & TAX SCHEDULES                                    */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'reports' && (
          <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <h2 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                  <i className="ri-file-chart-line text-indigo-600 text-xl" />
                  Statutory Fixed Asset Schedule (Balance Sheet & Tax Compliance)
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Gross Block, Accumulated Depreciation and Net Block breakdown per asset category as required by Indian Accounting Standards and Income Tax Act.
                </p>
              </div>

              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 cursor-pointer transition-colors shadow-sm"
              >
                <i className="ri-printer-line" />
                Print Schedule
              </button>
            </div>

            {scheduleReport && (
              <div className="border border-[#e2e8f0] rounded-xl overflow-hidden text-xs">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold border-b border-[#e2e8f0] uppercase">
                    <tr>
                      <th className="px-4 py-3">Asset Category</th>
                      <th className="px-4 py-3">Depr Method & Rate</th>
                      <th className="px-4 py-3 text-right">Gross Block (Cost)</th>
                      <th className="px-4 py-3 text-right">Accumulated Depreciation</th>
                      <th className="px-4 py-3 text-right">Net Block (Book Value)</th>
                      <th className="px-4 py-3 text-center">Assets Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono">
                    {scheduleReport.categories.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-sans font-bold text-[#1e293b]">{c.name}</td>
                        <td className="px-4 py-3 text-slate-600 font-sans">{c.depreciation_method} @ {c.it_depreciation_rate}%</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#1e293b]">{formatINR(c.gross_block)}</td>
                        <td className="px-4 py-3 text-right text-amber-700 font-semibold">{formatINR(c.total_depr)}</td>
                        <td className="px-4 py-3 text-right font-bold text-indigo-700">{formatINR(c.net_block)}</td>
                        <td className="px-4 py-3 text-center font-sans">{c.asset_count}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100/70 font-bold text-sm text-[#1e293b] border-t-2 border-slate-300">
                      <td className="px-4 py-3 font-sans">TOTAL FIXED ASSETS</td>
                      <td className="px-4 py-3 font-sans">—</td>
                      <td className="px-4 py-3 text-right">{formatINR(scheduleReport.totals.totalGrossBlock)}</td>
                      <td className="px-4 py-3 text-right text-amber-700">{formatINR(scheduleReport.totals.totalDepreciation)}</td>
                      <td className="px-4 py-3 text-right text-indigo-700">{formatINR(scheduleReport.totals.totalNetBlock)}</td>
                      <td className="px-4 py-3 text-center font-sans">{scheduleReport.totals.totalAssets}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: REGISTER NEW ASSET                                         */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {registerModalOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={() => setRegisterModalOpen(false)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-[#e2e8f0]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-slate-50/50">
                  <h2 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                    <i className="ri-add-box-line text-indigo-600" />
                    Register New Fixed Asset
                  </h2>
                  <button onClick={() => setRegisterModalOpen(false)} className="text-slate-500 hover:text-slate-700">
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                <form onSubmit={handleCreateAsset} className="flex-1 overflow-y-auto p-6 space-y-4 text-xs">
                  {/* Section 1: Identification */}
                  <div>
                    <h3 className="font-bold text-[#1e293b] border-b border-[#e2e8f0] pb-1 mb-2.5">
                      1. Asset Identification & Classification
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Asset Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. 50-Ton Hydraulic Press Machine"
                          value={newAsset.assetName}
                          onChange={(e) => setNewAsset({ ...newAsset, assetName: e.target.value })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Asset Category *</label>
                        <select
                          required
                          value={newAsset.categoryId}
                          onChange={(e) => {
                            const cat = categories.find((c) => c.id === e.target.value);
                            setNewAsset({
                              ...newAsset,
                              categoryId: e.target.value,
                              depreciationRate: cat ? cat.it_depreciation_rate : 15,
                              depreciationMethod: cat ? cat.depreciation_method : 'WDV',
                            });
                          }}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-semibold"
                        >
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} ({c.it_depreciation_rate}% WDV)
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Serial Number</label>
                        <input
                          type="text"
                          placeholder="e.g. HP50-2024-991"
                          value={newAsset.serialNumber}
                          onChange={(e) => setNewAsset({ ...newAsset, serialNumber: e.target.value })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Purchase & Capitalized Cost */}
                  <div>
                    <h3 className="font-bold text-[#1e293b] border-b border-[#e2e8f0] pb-1 mb-2.5">
                      2. Purchase Cost & Capitalization Details
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Purchase Date *</label>
                        <input
                          type="date"
                          required
                          value={newAsset.purchaseDate}
                          onChange={(e) => setNewAsset({ ...newAsset, purchaseDate: e.target.value })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Base Purchase Cost (₹) *</label>
                        <input
                          type="number"
                          required
                          placeholder="0.00"
                          value={newAsset.purchaseCost || ''}
                          onChange={(e) => setNewAsset({ ...newAsset, purchaseCost: Number(e.target.value) })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-mono font-semibold"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Installation / Freight (₹)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newAsset.additionalCost || ''}
                          onChange={(e) => setNewAsset({ ...newAsset, additionalCost: Number(e.target.value) })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Residual / Scrap Value (₹)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newAsset.scrapValue || ''}
                          onChange={(e) => setNewAsset({ ...newAsset, scrapValue: Number(e.target.value) })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5] font-mono"
                        />
                      </div>

                      <div className="col-span-2 bg-indigo-50/50 p-2 rounded-lg border border-indigo-200 flex items-center justify-between">
                        <span className="font-bold text-indigo-950">Total Capitalized Cost:</span>
                        <span className="font-mono font-bold text-indigo-700 text-sm">
                          {formatINR(Number(newAsset.purchaseCost || 0) + Number(newAsset.additionalCost || 0))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Location & Custodian */}
                  <div>
                    <h3 className="font-bold text-[#1e293b] border-b border-[#e2e8f0] pb-1 mb-2.5">
                      3. Location & Responsible Custodian
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Department</label>
                        <input
                          type="text"
                          placeholder="e.g. Production, IT, Logistics"
                          value={newAsset.department}
                          onChange={(e) => setNewAsset({ ...newAsset, department: e.target.value })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Location Details</label>
                        <input
                          type="text"
                          placeholder="e.g. Shop Floor Bay 2"
                          value={newAsset.locationDetail}
                          onChange={(e) => setNewAsset({ ...newAsset, locationDetail: e.target.value })}
                          className="w-full h-9 px-3 bg-white border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Capitalization JV Checkbox */}
                  <div className="border border-[#e2e8f0] p-3 rounded-xl bg-slate-50 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="cap_jv"
                      checked={newAsset.postCapitalizationJv}
                      onChange={(e) => setNewAsset({ ...newAsset, postCapitalizationJv: e.target.checked })}
                      className="cursor-pointer text-indigo-600 focus:ring-0"
                    />
                    <label htmlFor="cap_jv" className="text-[11px] font-bold text-slate-800 cursor-pointer">
                      Auto-post Capitalization Journal Voucher in General Ledger (Dr Asset / Cr Bank or Vendor)
                    </label>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setRegisterModalOpen(false)}
                      className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                      className="h-9 px-5 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700"
                    >
                      {loading ? 'Saving Asset...' : 'Save & Register Asset'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* SLIDE-OVER DRAWER: ASSET DETAIL & PROJECTION                      */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {detailDrawerOpen && selectedAssetId && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={() => setDetailDrawerOpen(false)} />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-2xl flex flex-col border-l border-[#e2e8f0]">
              <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0] bg-slate-50/50">
                <div>
                  <span className="font-mono text-xs font-bold text-indigo-600">
                    {assetDetail?.asset.asset_code}
                  </span>
                  <h2 className="text-base font-bold text-[#1e293b]">{assetDetail?.asset.asset_name}</h2>
                </div>
                <button
                  onClick={() => setDetailDrawerOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              {/* Drawer Tabs */}
              <div className="flex border-b border-[#e2e8f0] px-5 text-xs font-bold">
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'schedule', label: 'Depreciation Schedule' },
                  { key: 'timeline', label: 'Audit Timeline' },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setDetailTab(t.key as any)}
                    className={`py-3 px-3.5 border-b-2 transition-colors cursor-pointer ${
                      detailTab === t.key
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-5 text-xs space-y-4">
                {detailTab === 'overview' && assetDetail && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 p-3 rounded-xl border border-[#e2e8f0]">
                        <p className="text-[10px] text-slate-500 uppercase font-bold">Capitalized Cost</p>
                        <p className="font-mono font-bold text-sm text-[#1e293b] mt-0.5">
                          {formatINR(assetDetail.asset.total_capitalized_cost)}
                        </p>
                      </div>
                      <div className="bg-indigo-50/50 p-3 rounded-xl border border-indigo-200">
                        <p className="text-[10px] text-indigo-700 uppercase font-bold">Current Net Book Value</p>
                        <p className="font-mono font-bold text-sm text-indigo-700 mt-0.5">
                          {formatINR(assetDetail.asset.current_book_value)}
                        </p>
                      </div>
                    </div>

                    <div className="border border-[#e2e8f0] p-4 rounded-xl space-y-2 text-xs">
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span className="text-slate-500">Category:</span>
                        <span className="font-bold">{assetDetail.asset.category_name}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span className="text-slate-500">Depreciation Rule:</span>
                        <span className="font-bold">{assetDetail.asset.depreciation_method} @ {assetDetail.asset.depreciation_rate}%</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span className="text-slate-500">Purchase Date:</span>
                        <span className="font-bold">{formatDateIST(assetDetail.asset.purchase_date)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span className="text-slate-500">Location:</span>
                        <span className="font-bold">{assetDetail.asset.location_detail || assetDetail.asset.department}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">Responsible Custodian:</span>
                        <span className="font-bold">{assetDetail.asset.custodian_name || '—'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === 'schedule' && assetDetail && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      Multi-year projected depreciation schedule under {assetDetail.asset.depreciation_method} until scrap value ({formatINR(assetDetail.asset.scrap_value)}).
                    </p>
                    <div className="border border-[#e2e8f0] rounded-xl overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
                          <tr>
                            <th className="px-3 py-2">Year</th>
                            <th className="px-3 py-2 text-right">Opening BV</th>
                            <th className="px-3 py-2 text-right">Depreciation</th>
                            <th className="px-3 py-2 text-right">Closing BV</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                          {assetDetail.projectedSchedule.map((s) => (
                            <tr key={s.yearNumber}>
                              <td className="px-3 py-1.5 font-sans font-semibold">{s.financialYear}</td>
                              <td className="px-3 py-1.5 text-right">{formatINR(s.openingBookValue)}</td>
                              <td className="px-3 py-1.5 text-right text-amber-700">-{formatINR(s.depreciationAmount)}</td>
                              <td className="px-3 py-1.5 text-right font-bold text-indigo-700">{formatINR(s.closingBookValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {detailTab === 'timeline' && assetDetail && (
                  <div className="space-y-3">
                    {assetDetail.movements.length === 0 ? (
                      <p className="text-slate-400 text-center py-6">No movement events logged yet.</p>
                    ) : (
                      <div className="relative border-l-2 border-indigo-200 ml-3 space-y-4">
                        {assetDetail.movements.map((m) => (
                          <div key={m.id} className="relative pl-5 text-xs">
                            <div className="absolute -left-[7px] top-1 w-3 h-3 bg-indigo-600 rounded-full border-2 border-white shadow-sm" />
                            <p className="font-bold text-[#1e293b]">{m.movement_type}</p>
                            <p className="text-[10px] text-slate-400">{formatDateIST(m.movement_date)}</p>
                            <p className="text-slate-600 mt-0.5">{m.remarks}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}